import React, { useEffect, useState, useMemo } from 'react';
import { proyectosApi, entidadesApi, productosApi, catalogosApi, contratosApi, exportApi, usuariosApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, KanbanSquare, Table2, Pencil, Trash2, Briefcase, Building2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { ConfirmDeleteDialog } from '../components/ui/ConfirmDeleteDialog';
import { useDebounce } from '../hooks/useDebounce';
import { useSortable } from '../hooks/useSortable';
import { SortableHeader } from '../components/ui/SortableHeader';
import { EmptyState } from '../components/ui/EmptyState';
import { formatFecha } from '../utils/dateFormat';

const ESTADO_LABEL = {
  identificado: 'Identificado',
  en_prescripcion: 'En prescripción',
  prescrito: 'Prescrito / Homologado',
  en_licitacion: 'En licitación',
  ganado: 'Ganado',
  perdido: 'Perdido',
  cancelado: 'Cancelado',
  seguimiento_postventa: 'Seguimiento post-venta',
};
const ESTADO_COLOR = {
  identificado: 'bg-slate-100 text-slate-700',
  en_prescripcion: 'bg-blue-100 text-blue-700',
  prescrito: 'bg-teal-100 text-teal-700',
  en_licitacion: 'bg-amber-100 text-amber-700',
  ganado: 'bg-emerald-100 text-emerald-700',
  perdido: 'bg-rose-100 text-rose-700',
  cancelado: 'bg-ink-100 text-ink-500',
  seguimiento_postventa: 'bg-violet-100 text-violet-700',
};
const ESTADOS_ORDER = Object.keys(ESTADO_LABEL);

export default function Proyectos() {
  const { isAdmin, user } = useAuth();
  const canWrite = isAdmin || user?.rol === 'dir_comercial' || user?.rol === 'comercial' || user?.rol === 'agente_libre';
  const canExport = user?.rol === 'admin' || user?.rol === 'dir_comercial';
  const [items, setItems] = useState([]);
  const [view, setView] = useState('tabla'); // tabla | kanban
  const [q, setQ] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('__all__');
  const [empresaFilter, setEmpresaFilter] = useState('__all__');
  const [comercialFilter, setComercialFilter] = useState('__all__');
  const [usuarios, setUsuarios] = useState([]);
  const [dialog, setDialog] = useState({ open: false, data: null });
  const [entidades, setEntidades] = useState([]);
  const [productos, setProductos] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [confirmDel, setConfirmDel] = useState({ open: false, item: null });

  const verComercial = isAdmin || user?.rol === 'dir_comercial' || user?.rol === 'admin_comercial';

  const { sorted, sortKey, sortDir, toggle } = useSortable(
    items,
    'titulo',
    'asc',
    {
      prescriptor: (p) => p.entidad_prescriptor_nombre || '',
      comprador: (p) => p.entidad_comprador_nombre || '',
      importe: (p) => p.importe_estimado ?? -1,
      probabilidad: (p) => p.probabilidad_pct ?? -1,
      decision: (p) => p.fecha_decision || '',
    }
  );

  const blank = {
    titulo: '', codigo_expediente: '',
    entidad_prescriptor_id: '', contrato_id: '', entidad_comprador_id: '',
    contacto_principal_id: '', producto_ids: [], estado: 'identificado',
    importe_estimado: '', fecha_inicio_prevista: '', fecha_decision: '',
    probabilidad_pct: '', notas: '',
  };

  const load = async () => {
    const params = { q: qDebounced || undefined, estado: estadoFilter !== '__all__' ? estadoFilter : undefined };
    if (empresaFilter !== '__all__') params.entidad_id = empresaFilter;
    if (comercialFilter !== '__all__') params.comercial_asignado_id = comercialFilter;
    const [ps, es, prs, cs] = await Promise.all([
      proyectosApi.list(params),
      entidadesApi.list({ limit: 500 }),
      productosApi.list({ activo: true, limit: 500 }),
      contratosApi.list(),
    ]);
    setItems(ps); setEntidades(es); setProductos(prs); setContratos(cs);
  };
  const qDebounced = useDebounce(q, 300);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [qDebounced, estadoFilter, empresaFilter, comercialFilter]);

  useEffect(() => {
    (async () => {
      if (verComercial) {
        try { setUsuarios(await usuariosApi.list({ estado: 'activo' })); } catch {}
      }
    })();
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    try {
      const d = { ...dialog.data };
      // Sanear
      for (const k of ['importe_estimado', 'probabilidad_pct']) {
        if (d[k] === '' || d[k] == null) delete d[k]; else d[k] = Number(d[k]);
      }
      for (const k of ['fecha_inicio_prevista', 'fecha_decision']) {
        if (!d[k]) delete d[k];
      }
      for (const k of ['entidad_prescriptor_id', 'contrato_id', 'entidad_comprador_id', 'contacto_principal_id']) {
        if (!d[k]) delete d[k];
      }
      if (d.id) {
        const id = d.id; delete d.id;
        await proyectosApi.update(id, d);
        toast.success('Proyecto actualizado');
      } else {
        await proyectosApi.create(d);
        toast.success('Proyecto creado');
      }
      setDialog({ open: false, data: null });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const remove = (item) => setConfirmDel({ open: true, item });
  const confirmarBorrar = async () => {
    try {
      await proyectosApi.delete(confirmDel.item.id);
      toast.success('Proyecto borrado');
      load();
    } catch (e) { toast.error(e.message); }
  };

  const move = async (p, nuevoEstado) => {
    if (p.estado === nuevoEstado) return;
    try {
      await proyectosApi.update(p.id, { estado: nuevoEstado });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const grouped = useMemo(() => {
    const g = Object.fromEntries(ESTADOS_ORDER.map((e) => [e, []]));
    items.forEach((i) => { (g[i.estado] || (g[i.estado] = [])).push(i); });
    return g;
  }, [items]);

  return (
    <div className="space-y-6" data-testid="proyectos-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-ink-900 font-display tracking-tight">Proyectos / Oportunidades</h1>
          <p className="text-sm text-ink-500 mt-1">Ciclo comercial B2B2G: prescriptor · destino · comprador</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex rounded-md border border-ink-200 overflow-hidden">
            <button
              className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${view === 'tabla' ? 'bg-ink-900 text-white' : 'bg-white text-ink-600'}`}
              onClick={() => setView('tabla')}
              data-testid="toggle-tabla"
            >
              <Table2 className="w-3.5 h-3.5" /> Tabla
            </button>
            <button
              className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${view === 'kanban' ? 'bg-ink-900 text-white' : 'bg-white text-ink-600'}`}
              onClick={() => setView('kanban')}
              data-testid="toggle-kanban"
            >
              <KanbanSquare className="w-3.5 h-3.5" /> Kanban
            </button>
          </div>
          {canExport && (
            <Button variant="outline" onClick={async () => { try { await exportApi.descargar('proyectos'); toast.success('CSV descargado'); } catch (e) { toast.error(e.message); } }} data-testid="btn-export-proyectos">
              <Download className="w-4 h-4 mr-2" /> Exportar
            </Button>
          )}
          {canWrite && (
            <Button onClick={() => setDialog({ open: true, data: blank })} data-testid="btn-nuevo-proyecto">
              <Plus className="w-4 h-4 mr-2" /> Nuevo proyecto
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-3 items-center flex-wrap">
        <Input placeholder="Buscar por título o expediente..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-md" data-testid="proyectos-filter-q" />
        <Select value={estadoFilter} onValueChange={setEstadoFilter}>
          <SelectTrigger className="w-56" data-testid="proyectos-filter-estado"><SelectValue placeholder="Estado" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos los estados</SelectItem>
            {ESTADOS_ORDER.map((e) => <SelectItem key={e} value={e}>{ESTADO_LABEL[e]}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={empresaFilter} onValueChange={setEmpresaFilter}>
          <SelectTrigger className="w-64" data-testid="proyectos-filter-empresa"><SelectValue placeholder="Empresa" /></SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="__all__">Todas las empresas</SelectItem>
            {entidades.map((e) => <SelectItem key={e.id} value={e.id}>{e.razon_social}</SelectItem>)}
          </SelectContent>
        </Select>
        {verComercial && (
          <Select value={comercialFilter} onValueChange={setComercialFilter}>
            <SelectTrigger className="w-56" data-testid="proyectos-filter-comercial"><SelectValue placeholder="Comercial" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todos los comerciales</SelectItem>
              {usuarios.map((u) => <SelectItem key={u.id} value={u.id}>{u.nombre} {u.apellidos || ''}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* VISTA TABLA */}
      {view === 'tabla' && (
        <div className="bg-white rounded-lg border border-ink-200 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-ink-50 text-ink-600 uppercase text-xs">
              <tr>
                <SortableHeader label="Título"      sortKey="titulo"            currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Expediente"  sortKey="codigo_expediente" currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Prescriptor" sortKey="prescriptor"       currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Comprador"   sortKey="comprador"         currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <SortableHeader label="Estado"      sortKey="estado"            currentKey={sortKey} dir={sortDir} onToggle={toggle} align="center" />
                <SortableHeader label="Importe"     sortKey="importe"           currentKey={sortKey} dir={sortDir} onToggle={toggle} align="right" />
                <SortableHeader label="Prob."       sortKey="probabilidad"      currentKey={sortKey} dir={sortDir} onToggle={toggle} align="center" />
                <SortableHeader label="Decisión"    sortKey="decision"          currentKey={sortKey} dir={sortDir} onToggle={toggle} />
                <th className="w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-100">
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-0 py-0">
                    <EmptyState
                      compact
                      icon={Briefcase}
                      title="Sin proyectos en este filtro"
                      description="Prueba a quitar algún filtro o crea un nuevo proyecto."
                      testid="proyectos-empty"
                    />
                  </td>
                </tr>
              )}
              {sorted.map((p) => (
                <tr key={p.id} className="hover:bg-ink-50" data-testid={`proyecto-row-${p.id}`}>
                  <td className="px-4 py-3 font-medium text-ink-900">{p.titulo}</td>
                  <td className="px-4 py-3 text-ink-500 font-mono text-xs">{p.codigo_expediente || '—'}</td>
                  <td className="px-4 py-3 text-ink-600">{p.entidad_prescriptor_nombre || '—'}</td>
                  <td className="px-4 py-3 text-ink-600">{p.entidad_comprador_nombre || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block px-2 py-0.5 rounded text-xs ${ESTADO_COLOR[p.estado] || ''}`}>{ESTADO_LABEL[p.estado]}</span>
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums">{p.importe_estimado != null ? `${p.importe_estimado.toLocaleString('es-ES')} €` : '—'}</td>
                  <td className="px-4 py-3 text-center">{p.probabilidad_pct != null ? `${p.probabilidad_pct}%` : '—'}</td>
                  <td className="px-4 py-3 text-ink-500">{formatFecha(p.fecha_decision) || '—'}</td>
                  <td className="px-4 py-3 text-right">
                    {canWrite && (
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" onClick={() => setDialog({ open: true, data: { ...p, producto_ids: p.producto_ids || [] } })}>
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {(isAdmin || user?.rol === 'dir_comercial') && (
                          <Button variant="ghost" size="sm" onClick={() => remove(p)} className="text-rose-600">
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* VISTA KANBAN */}
      {view === 'kanban' && (
        <div className="flex gap-3 overflow-x-auto pb-4" data-testid="kanban-proyectos">
          {ESTADOS_ORDER.map((e) => (
            <div
              key={e}
              className="min-w-[260px] w-[260px] bg-ink-50 rounded-lg p-3 flex flex-col"
              onDragOver={(ev) => ev.preventDefault()}
              onDrop={(ev) => {
                const pid = ev.dataTransfer.getData('text/plain');
                const p = items.find((x) => x.id === pid);
                if (p) move(p, e);
              }}
              data-testid={`kanban-col-${e}`}
            >
              <div className={`px-2 py-1 rounded text-xs font-semibold mb-2 ${ESTADO_COLOR[e]}`}>
                {ESTADO_LABEL[e]} <span className="opacity-60">({grouped[e]?.length || 0})</span>
              </div>
              <div className="space-y-2 flex-1 overflow-y-auto">
                {(grouped[e] || []).map((p) => (
                  <div
                    key={p.id}
                    draggable={canWrite}
                    onDragStart={(ev) => ev.dataTransfer.setData('text/plain', p.id)}
                    onClick={() => setDialog({ open: true, data: { ...p, producto_ids: p.producto_ids || [] } })}
                    className="bg-white rounded-md p-3 border border-ink-200 shadow-sm cursor-pointer hover:border-brand-400 text-xs"
                  >
                    <p className="font-semibold text-ink-900 mb-1 truncate">{p.titulo}</p>
                    {p.codigo_expediente && <p className="text-ink-500 font-mono text-[10px]">{p.codigo_expediente}</p>}
                    {p.entidad_prescriptor_nombre && <p className="text-ink-600 mt-1 flex items-center gap-1"><Building2 className="w-3 h-3 shrink-0" />{p.entidad_prescriptor_nombre}</p>}
                    <div className="flex items-center justify-between mt-2 text-[10px]">
                      {p.importe_estimado != null && <span className="text-ink-500 tabular-nums">{p.importe_estimado.toLocaleString('es-ES')} €</span>}
                      {p.probabilidad_pct != null && <span className="text-brand-600 font-semibold">{p.probabilidad_pct}%</span>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog crear/editar */}
      <Dialog open={dialog.open} onOpenChange={(v) => setDialog({ open: v, data: v ? dialog.data : null })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Briefcase className="w-4 h-4" />{dialog.data?.id ? 'Editar proyecto' : 'Nuevo proyecto'}</DialogTitle>
          </DialogHeader>
          {dialog.data && (
            <div className="space-y-3">
              <div>
                <Label>Título *</Label>
                <Input value={dialog.data.titulo} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, titulo: e.target.value } }))} data-testid="input-titulo" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Código expediente</Label>
                  <Input value={dialog.data.codigo_expediente || ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, codigo_expediente: e.target.value } }))} />
                </div>
                <div>
                  <Label>Estado</Label>
                  <Select value={dialog.data.estado} onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, estado: v } }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{ESTADOS_ORDER.map((e) => <SelectItem key={e} value={e}>{ESTADO_LABEL[e]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Prescriptor (gestora / OT)</Label>
                <EntidadCombo value={dialog.data.entidad_prescriptor_id} options={entidades} onChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, entidad_prescriptor_id: v } }))} />
              </div>
              <div>
                <Label>Comprador (distribuidor / cliente final)</Label>
                <EntidadCombo value={dialog.data.entidad_comprador_id} options={entidades} onChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, entidad_comprador_id: v } }))} />
              </div>
              <div>
                <Label>Contrato destino</Label>
                <Select value={dialog.data.contrato_id || '__none__'} onValueChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, contrato_id: v === '__none__' ? '' : v } }))}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {contratos.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre} · {c.gestora_nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Importe estimado (€)</Label>
                  <Input type="number" step="0.01" value={dialog.data.importe_estimado ?? ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, importe_estimado: e.target.value } }))} />
                </div>
                <div>
                  <Label>Fecha decisión</Label>
                  <Input type="date" value={dialog.data.fecha_decision || ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, fecha_decision: e.target.value } }))} />
                </div>
                <div>
                  <Label>Probabilidad (%)</Label>
                  <Input type="number" min="0" max="100" value={dialog.data.probabilidad_pct ?? ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, probabilidad_pct: e.target.value } }))} />
                </div>
              </div>
              <div>
                <Label>Productos involucrados</Label>
                <MultiSelectProductos value={dialog.data.producto_ids || []} options={productos} onChange={(v) => setDialog((d) => ({ ...d, data: { ...d.data, producto_ids: v } }))} />
              </div>
              <div>
                <Label>Notas</Label>
                <Textarea rows={2} value={dialog.data.notas || ''} onChange={(e) => setDialog((d) => ({ ...d, data: { ...d.data, notas: e.target.value } }))} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, data: null })}>Cancelar</Button>
            <Button onClick={save} data-testid="btn-guardar-proyecto">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={confirmDel.open}
        onOpenChange={(o) => setConfirmDel((s) => ({ ...s, open: o }))}
        resource="proyecto"
        itemName={confirmDel.item?.titulo}
        onConfirm={confirmarBorrar}
        testidPrefix="confirm-del-proyecto"
      />
    </div>
  );
}

const EntidadCombo = ({ value, options, onChange }) => (
  <Select value={value || '__none__'} onValueChange={(v) => onChange(v === '__none__' ? '' : v)}>
    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
    <SelectContent className="max-h-60">
      <SelectItem value="__none__">—</SelectItem>
      {options.map((e) => <SelectItem key={e.id} value={e.id}>{e.nombre_comercial || e.razon_social}</SelectItem>)}
    </SelectContent>
  </Select>
);

const MultiSelectProductos = ({ value, options, onChange }) => {
  const toggle = (id) => {
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };
  return (
    <div className="flex flex-wrap gap-1.5 p-2 border border-ink-200 rounded-md max-h-32 overflow-y-auto">
      {options.length === 0 && <span className="text-xs text-ink-400">Sin productos — crea productos primero</span>}
      {options.map((p) => {
        const on = value.includes(p.id);
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => toggle(p.id)}
            className={`px-2 py-0.5 rounded text-xs transition-colors ${on ? 'bg-brand-500 text-white' : 'bg-ink-100 text-ink-700 hover:bg-ink-200'}`}
          >
            {p.referencia}
          </button>
        );
      })}
    </div>
  );
};
